import React, { useState, useRef } from "react";
import axios from "axios";
import { Turnstile } from "@marsidev/react-turnstile";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const turnstileRef = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!token) {
      toast.error("Please complete the verification below.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, token });
      toast.success("Message sent — Marta will be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
      setToken(null);
      turnstileRef.current?.reset();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Something went wrong. Please try again."
      );
      setToken(null);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-gray-600 focus:border-[#b533ff] focus:outline-none transition-colors duration-300";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-[#0a0a0c] overflow-hidden"
    >
      <div className="absolute left-1/2 -translate-x-1/2 top-0 w-[600px] h-[600px] rounded-full bg-[#b533ff]/10 blur-[160px]" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-[#E879F9] font-bold mb-4">
                04 — Let's talk
              </p>
              <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl leading-[0.9]">
                Put me on <span className="text-[#b533ff] text-glow">the bill.</span>
              </h2>
              <p className="mt-6 text-base text-gray-300 font-light leading-relaxed max-w-md">
                Touring, festivals, hospitality or box office — tell me what you're planning
                and I'll get back to you.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="bg-[#121216] border border-white/10 rounded-2xl p-7 sm:p-10 space-y-7"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <input
                    data-testid="contact-input-name"
                    className={inputCls}
                    placeholder="Your name"
                    value={form.name}
                    onChange={update("name")}
                  />
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    className={inputCls}
                    placeholder="Email address"
                    value={form.email}
                    onChange={update("email")}
                  />
                </div>
                <input
                  data-testid="contact-input-subject"
                  className={inputCls}
                  placeholder="Subject (tour, festival, ticketing…)"
                  value={form.subject}
                  onChange={update("subject")}
                />
                <textarea
                  data-testid="contact-input-message"
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder="Tell me about the project"
                  value={form.message}
                  onChange={update("message")}
                />
                <div data-testid="contact-turnstile">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    options={{ theme: "dark" }}
                    onSuccess={(t) => setToken(t)}
                    onExpire={() => setToken(null)}
                    onError={() => setToken(null)}
                  />
                </div>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#b533ff] text-white font-semibold uppercase tracking-[0.15em] text-sm hover:shadow-[0_0_30px_rgba(181,51,255,0.7)] transition-shadow duration-300 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Send message
                      <Send size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
